import { InjectedFormikProps } from 'formik';
import React, { useContext } from 'react';
import { Divider, Icon } from 'semantic-ui-react';

import { AuthenticationContext } from '@modules/auth/components/context';

import DeleteUser from '@src/components/DeleteUser';
import { EmailFormField } from '@modules/common/components/Field';
import FormInputField from '@modules/common/components/Field/FormInputField';
import FormErrors from '@src/components/Form/common/FormErrors';
import useCopyToClipboard from '@src/hooks/CopyToClipboard';
import Popup from '@src/components/common/Popup';
import PrimaryButton from '@modules/common/components/buttons/PrimaryButton';
import PrimaryForm from '@modules/common/components/PrimaryForm';
import { CountryListType } from '@src/constants/countries';

import { UserFormProps, UserFormValues } from '.';
import ConnectedCountryPicker from '../../../Billing/components/billing/billing-info/CountryPicker/ConnectedCountryPicker';

const UserForm: React.SFC<InjectedFormikProps<UserFormProps, UserFormValues>> = ({
  errors,
  handleChange,
  values: { givenName, familyName, email, countryCode },
  isSubmitting,
  handleSubmit,
  changePasswordClick,
  setFieldValue,
  configurableUser,
  logout,
  accountId,
  disabled,
}) => {
  const anyErrors: any = errors;

  const { copyAction } = useCopyToClipboard(configurableUser.id);
  const { copyAction: copyAccountId } = useCopyToClipboard(accountId);
  const readonly = useContext(AuthenticationContext).readonly;

  return (
    <PrimaryForm style={{ padding: '2rem' }}>
      <FormErrors errors={anyErrors.non_field_errors} />

      <FormInputField
        fieldLabel="First Name"
        errors={anyErrors}
        handleChange={handleChange}
        value={givenName}
        fieldName="givenName"
        icon="user"
        iconPosition="left"
        readOnly={readonly}
      />

      <FormInputField
        fieldLabel="Last Name"
        errors={anyErrors}
        handleChange={handleChange}
        value={familyName}
        fieldName="familyName"
        icon="user"
        iconPosition="left"
        readOnly={readonly}
      />

      <EmailFormField
        fieldLabel="E-mail"
        errors={anyErrors}
        readOnly
        value={email}
        fieldName="email"
        icon="user"
        iconPosition="left"
      />

      <ConnectedCountryPicker
        setCountry={country => setFieldValue('countryCode', country)}
        country={countryCode}
        label="Country"
        useIp={false}
        type={CountryListType.USER_PROFILE}
      />

      <FormInputField
        fieldLabel={
          <>
            User ID &nbsp;
            <Popup
              trigger={<Icon name="warning sign" />}
              content="Only give your ID to the people you trust!"
            />
          </>
        }
        value={configurableUser.id}
        readOnly
        action={copyAction}
        fieldName="userId"
        icon="id card"
        iconPosition="left"
      />

      {accountId && (
        <FormInputField
          fieldLabel={
            <>
              Account ID &nbsp;
              <Popup
                trigger={<Icon name="warning sign" />}
                content="Only give your Account ID to the people you trust! Account ID is used to share your instances."
              />
            </>
          }
          value={accountId}
          readOnly
          action={copyAccountId}
          fieldName="accountId"
          icon="id card"
          iconPosition="left"
        />
      )}

      {!readonly && (
        <>
          <PrimaryButton
            loading={isSubmitting}
            content="Save Changes"
            onClick={handleSubmit as any}
            icon="save"
            disabled={disabled}
          />

          <Divider />

          <DeleteUser logout={logout} userId={configurableUser.id} />
        </>
      )}
    </PrimaryForm>
  );
};

export { UserForm };

export default React.memo(UserForm);
